import cx from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type ClickPopoverPlacement = "above" | "below";

export type ClickPopoverProps = {
  /** Content shown in the page; clicking it toggles the popover. */
  children: React.ReactNode;
  /** Content rendered in the fixed overlay. */
  content: React.ReactNode;
  /** Placement of the overlay relative to the trigger. Default: "above". */
  placement?: ClickPopoverPlacement;
  /** Extra class name(s) for the trigger wrapper. */
  triggerClassName?: string;
  /** Extra class name(s) for the overlay. */
  overlayClassName?: string;
};

export default function ClickPopover({
  children,
  content,
  placement = "above",
  triggerClassName,
  overlayClassName,
}: ClickPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [isOpen]);

  const handleClick = () => {
    setIsOpen((current) => !current);

    const refRect = ref.current!.getBoundingClientRect();
    const popoverRect = popoverRef.current!.getBoundingClientRect();
    const popoverWidth = popoverRect.width;
    const popoverHeight = popoverRect.height;

    const MARGIN = 40;
    let left = refRect.left + refRect.width / 2 - popoverWidth / 2;
    if (left < MARGIN) {
      left = MARGIN;
    }
    if (left + popoverWidth > window.innerWidth - MARGIN) {
      left = window.innerWidth - popoverWidth - MARGIN;
    }

    let top: number;
    if (placement === "above") {
      top = refRect.top - popoverHeight;
    } else {
      top = refRect.bottom;
    }
    if (top < MARGIN) {
      top = MARGIN;
    }
    if (top + popoverHeight > window.innerHeight - MARGIN) {
      top = window.innerHeight - popoverHeight - MARGIN;
    }
    setPosition({ top, left });
  };

  return (
    <div className="relative">
      <div
        ref={ref}
        className={cx(
          "inline-block border border-gray-200 rounded-md p-2 hover:bg-gray-100 cursor-pointer",
          triggerClassName,
          isOpen && "bg-gray-100",
        )}
        onClick={handleClick}
      >
        {children}
      </div>
      {createPortal(
        <div
          ref={popoverRef}
          className={cx(
            "fixed p-2 gap-4 bg-white rounded-md shadow-md z-1000 flex flex-col border border-gray-200",
            overlayClassName,
          )}
          style={{
            top: position?.top,
            left: position?.left,
            visibility: isOpen ? "visible" : "hidden",
            opacity: isOpen ? 1 : 0,
            pointerEvents: isOpen ? "auto" : "none",
          }}
        >
          {content}
        </div>,
        document.body
      )}
    </div>
  );
}
